import { useEffect, useRef } from "react";

/**
 * Iter212 → Iter245 — Polling silencioso da versão do app.
 *
 * ═══════════ Iter245 — Decisão de produto ═══════════
 * Zero UI visível sobre incompatibilidade de cache. Usuários em redes
 * corporativas rígidas (Ministério, tribunais) sofrem com modais/toasts
 * de "Nova versão disponível" que os empurram para reloads que o proxy
 * cacheia, gerando telas brancas.
 *
 * A partir do Iter245:
 *   - **NENHUM toast, banner ou modal** de "nova versão".
 *   - Continuamos apenas observando ``/api/build-hash`` UMA VEZ no boot
 *     para publicar ``window.__JT_BUILD_HASH__`` — outros módulos usam
 *     esse valor para debugging silencioso e para o helper de reload
 *     proxy-proof (exposto como ``window.__jt_forceProxyProofReload``).
 *   - O navegador do usuário continua na versão que já tem em cache até
 *     que o Service Worker naturalmente decida atualizar. Zero fricção.
 *   - Se um ChunkLoadError disparar em segundo plano, o instrument.js
 *     fará um reload silencioso (uma tentativa, com cache-bust por URL).
 *     Sem interrupção visual.
 */
const BUILD_HASH_ENDPOINT = "/api/build-hash";

export function useBuildVersionCheck() {
  const bootedRef = useRef(false);

  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;

    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return; // dev local sem env — silêncio total

    const fetchBuildHash = async () => {
      try {
        const bust = Date.now();
        const res = await fetch(
          `${backend}${BUILD_HASH_ENDPOINT}?_=${bust}`,
          { cache: "no-store", credentials: "omit" },
        );
        if (!res.ok) return null;
        const data = await res.json();
        return data?.hash || null;
      } catch (_) {
        return null;
      }
    };

    // ``window.__jt_forceProxyProofReload`` continua exposto porque o
    // instrument.js pode dispará-lo silenciosamente em caso de chunk
    // error. Nunca é acionado por UI visível — só por lógica interna.
    try {
      window.__jt_forceProxyProofReload = () => {
        fetchBuildHash().then((newHash) => {
          try {
            const url = new URL(window.location.href);
            url.searchParams.delete("_v");
            url.searchParams.set("_v", newHash || String(Date.now()));
            window.location.href = url.toString();
          } catch (_) {
            try { window.location.reload(); } catch (__) { /* noop */ }
          }
        });
      };
    } catch (_) { /* window inacessível — noop */ }

    // Boot: registra o hash observado para debug futuro.
    (async () => {
      const id = await fetchBuildHash();
      if (!id) return;
      try {
        document.documentElement.dataset.buildId = id;
        window.__JT_BUILD_HASH__ = id;
      } catch (_) { /* SSR / edge — ignora */ }
    })();
  }, []);
}
